import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api, setUnauthorizedHandler } from "../api/client";
import logger from "../components/utils/logger";

// ---------------------------------------------------------------------------
// The logged-in session: `{ token, member }`, the same shape /api/login returns.
//
// Only the token is persisted. On load it's exchanged for a fresh session via
// /api/me, so a refresh keeps the user logged in and a revoked token doesn't.
// ---------------------------------------------------------------------------

const TOKEN_KEY = "familyToken";

function readStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable (private mode etc.) — the session just won't survive a refresh
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSessionState] = useState(null);
  // "loading" until a stored token has been checked
  const [status, setStatus] = useState(() =>
    readStoredToken() ? "loading" : "anonymous",
  );

  const setSession = useCallback((newSession) => {
    storeToken(newSession?.token ?? null);
    setSessionState(newSession);
    setStatus(newSession ? "authenticated" : "anonymous");
  }, []);

  const clearSession = useCallback(() => setSession(null), [setSession]);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) return;

    api
      .get("/family/api/me", token)
      .then((me) => setSession(me))
      .catch((err) => {
        logger.log("stored token rejected:", err);
        clearSession();
      });
  }, [setSession, clearSession]);

  const login = useCallback(
    async (username, password) => {
      const newSession = await api.post("/family/api/login", null, {
        username,
        password,
      });
      setSession(newSession);
      return newSession;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    const token = session?.token;
    clearSession();
    if (!token) return;
    try {
      await api.post("/family/api/logout", token);
    } catch (err) {
      // already logged out locally; a failure here just leaves the token valid server side
      logger.log("logout request failed:", err);
    }
  }, [session, clearSession]);

  const value = useMemo(
    () => ({ session, status, login, logout, setSession }),
    [session, status, login, logout, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}
